import React from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const HeroSection = ({ data, onCTAClick }) => {
  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-amber-600/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4wNSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        {/* Badge */}
        <div className="inline-flex items-center px-4 py-2 rounded-full bg-amber-600/20 border border-amber-600/30 backdrop-blur-sm mb-8 animate-fade-in">
          <div className="w-2 h-2 bg-amber-400 rounded-full mr-2 animate-pulse"></div>
          <span className="text-amber-100 text-sm font-medium">Trusted Worldwide</span>
        </div>

        {/* Main heading */}
        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight animate-slide-up">
          {data.title}
          <span className="block mt-2 bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">
            For Your Projects
          </span>
        </h1>

        {/* Subtitle */}
        <p className="text-xl md:text-2xl text-slate-300 mb-4 max-w-3xl mx-auto animate-slide-up" style={{ animationDelay: '0.1s' }}>
          {data.subtitle}
        </p>

        {/* Description */}
        <p className="text-lg text-slate-400 mb-12 max-w-2xl mx-auto animate-slide-up" style={{ animationDelay: '0.2s' }}>
          {data.description}
        </p>

        {/* Trust indicators */}
        <div className="flex flex-wrap justify-center items-center gap-6 mb-12 animate-slide-up" style={{ animationDelay: '0.3s' }}>
          <div className="flex items-center text-slate-300">
            <CheckCircle className="w-5 h-5 text-amber-500 mr-2" />
            <span>Premium Quality</span>
          </div>
          <div className="flex items-center text-slate-300">
            <CheckCircle className="w-5 h-5 text-amber-500 mr-2" />
            <span>Global Shipping</span>
          </div>
          <div className="flex items-center text-slate-300">
            <CheckCircle className="w-5 h-5 text-amber-500 mr-2" />
            <span>Competitive Prices</span>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-slide-up" style={{ animationDelay: '0.4s' }}>
          <Button 
            onClick={onCTAClick}
            className="group bg-amber-600 hover:bg-amber-700 text-white font-semibold px-8 py-6 text-lg rounded-lg shadow-2xl hover:shadow-amber-600/50 transition-all duration-300 transform hover:scale-105"
          >
            {data.cta}
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Button>
          <Button 
            onClick={onCTAClick}
            variant="outline"
            className="px-8 py-6 text-lg rounded-lg border-2 border-slate-600 text-slate-200 hover:bg-slate-800 hover:border-amber-600 transition-all duration-300"
          >
            Contact Us
          </Button>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-slate-500 flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-amber-500 rounded-full"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
